import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import Chip from '@mui/joy/Chip';
import Dropdown from '@mui/joy/Dropdown';
import IconButton from '@mui/joy/IconButton';
import Input from '@mui/joy/Input';
import ListDivider from '@mui/joy/ListDivider';
import Menu from '@mui/joy/Menu';
import MenuButton from '@mui/joy/MenuButton';
import MenuItem from '@mui/joy/MenuItem';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import Option from '@mui/joy/Option';
import Select from '@mui/joy/Select';
import Typography from '@mui/joy/Typography';
import lodash from 'lodash';
import { observer } from 'mobx-react-lite';

import ContrastButton from '../components/ContrastButton';
import DynamicTable, { TableColumn } from '../components/DynamicTable';
import EmailInvitesModal from '../components/EmailInvitesModal';
import GeneralModal from '../components/GeneralModal';
import TableHeader from '../components/TableHeader';
import Invitation from '../models/Invitation';
import Role from '../models/Role';
import { CompanyService } from '../services/CompanyService';
import { OrganizationUser, User } from '../services/types/api.generated';
import { UserRoleService } from '../services/UserRoleService';
import { UserService } from '../services/UserService';
import organizationStore from '../stores/OrganizationStore';
import sessionStore from '../stores/SessionStore';
import { UserRolesRow, UserRolesRowTable } from '../types/UserRolesRow';

const Settings = observer(() => {
  const { selectedOrg } = organizationStore;
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRows, setSelectedRows] = useState<UserRolesRow[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState<'manage' | 'permissions'>('manage');
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [isRoleDialogOpen, setIsRoleDialogOpen] = useState(false);
  const [roleDialogUserId, setRoleDialogUserId] = useState<string | null>(null);
  const [roleDialogSelectedRole, setRoleDialogSelectedRole] = useState<string | null>(null);
  const [organizationData, setOrganizationData] = useState<{
    users: OrganizationUser[];
    invitations: Invitation[];
  }>({ users: [], invitations: [] });
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const { t } = useTranslation();

  // For the Update Permissions dropdown, we should use the roles the current user can assign
  const assignableRoles = sessionStore.user?.assignable_roles || [];
  const [debouncedQuery, setDebouncedQuery] = useState<string>('');
  const fetchOrganizationData = async () => {
    if (selectedOrg !== null) {
      try {
        const response = await CompanyService.getOrganizationDetails(selectedOrg);
        setOrganizationData(response.data);
        setSelectedRows([]);
      } catch {
        toast.error(t('settings.errors.fetch'));
      }
    }
  };

  useEffect(() => {
    fetchOrganizationData();
  }, [selectedOrg]);

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedQuery(searchQuery), 300);
    return () => clearTimeout(handle);
  }, [searchQuery]);

  useEffect(() => {
    const lastUserEmail = localStorage.getItem(sessionStore.USER_EMAIL_KEY);

    if (lastUserEmail && lastUserEmail !== sessionStore.user?.email) {
      organizationStore.clearSelectedOrg();
    }
  }, [sessionStore.user]);

  const openRoleDialog = (rowId: string) => {
    setRoleDialogUserId(rowId);
    setRoleDialogSelectedRole(null);
    setIsRoleDialogOpen(true);
  };

  const handlePerRowRemove = (rowId: string) => {
    setSelectedRows([{ id: rowId, email: '', roles: [] } as unknown as UserRolesRow]);
    setModalType('manage');
    setIsModalOpen(true);
  };

  const columns: TableColumn<UserRolesRowTable, keyof UserRolesRowTable>[] = [
    { header: 'User', key: 'name' },
    { header: 'Email', key: 'email' },
    { header: 'Roles', key: 'roles' },
    {
      header: t('general_button_labels.actions'),
      key: 'actions',
      render: (_value, row) => (
        <Dropdown>
          <MenuButton
            variant='plain'
            slots={{ root: IconButton }}
            slotProps={{ root: { size: 'sm', color: 'neutral' } }}
          >
            <MoreVertIcon fontSize='small' />
          </MenuButton>
          <Menu>
            <MenuItem onClick={() => openRoleDialog(row.id as string)}>{t('settings.update_perms')}</MenuItem>
            <ListDivider />
            <MenuItem color='danger' onClick={() => handlePerRowRemove(row.id as string)}>
              {t('settings.remove_users', { rows: 1, msg: 'User' })}
            </MenuItem>
          </Menu>
        </Dropdown>
      )
    }
  ];

  const data: UserRolesRowTable[] = React.useMemo(() => {
    const users = organizationData.users.map((orgUser) => ({
      id: orgUser.id!.toString(),
      name: `${lodash.capitalize(orgUser.first_name ?? '')} ${lodash.capitalize(orgUser.last_name ?? '')}`,
      email: orgUser.email!,
      roles: (() => {
        const roleChips = (orgUser.roles || []).map((role) => (
          <Chip key={role} variant='soft'>
            {role}
          </Chip>
        ));
        if (orgUser.is_owner) {
          roleChips.push(
            <Chip key='owner' variant='soft'>
              {t('settings.owner')}
            </Chip>
          );
        }
        return roleChips;
      })()
    }));

    const invitations = organizationData.invitations.map((invitation) => ({
      id: invitation.user.toString(),
      email: invitation.user_email,
      roles: invitation.roles.map((role) => (
        <Chip key={role} variant='soft'>
          {role} (Invite Sent)
        </Chip>
      ))
    }));

    const sortedData = [...users, ...invitations];
    if (sessionStore.user) {
      const loggedInUserIndex = sortedData.findIndex(
        (user) => user.id.toString() === sessionStore.user?.id?.toString()
      );
      if (loggedInUserIndex > -1) {
        const [loggedInUser] = sortedData.splice(loggedInUserIndex, 1);
        sortedData.unshift(loggedInUser);
      }
    }

    return sortedData;
  }, [organizationData, sessionStore.user]);

  const filteredData = data.filter((row) =>
    columns.some((column) =>
      row[column.key as keyof typeof row]?.toString().toLowerCase().includes(debouncedQuery.toLowerCase())
    )
  );

  const handleSelectionChange = (localSelectedRows: any[]) => {
    setSelectedRows(localSelectedRows);
  };

  const handleOpenModal = (type: 'manage' | 'permissions') => {
    setModalType(type);
    setIsModalOpen(true);
  };

  const closeRoleDialog = () => {
    setIsRoleDialogOpen(false);
    setRoleDialogUserId(null);
    setRoleDialogSelectedRole(null);
  };

  const handleSaveRoleDialog = async () => {
    if (!roleDialogUserId || !roleDialogSelectedRole) return;
    try {
      await UserRoleService.bulkUpdateRoles([roleDialogUserId], parseInt(roleDialogSelectedRole, 10));
      toast.success(t('settings.user_perms_updated'));
      closeRoleDialog();
      fetchOrganizationData();
    } catch {
      toast.error(t('settings.update_perms'));
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleRemoveUsers = async () => {
    const idsToDelete = selectedRows.map((row) => row.id);

    try {
      await UserService.bulkRemoveOrganization(idsToDelete, selectedOrg!);
      toast.success(t('settings.user_removed'));
      setSelectedRows([]);
      setIsModalOpen(false);
      fetchOrganizationData();
    } catch {
      toast.error(t('settings.errors.remove_users'));
    }
  };

  const handleUpdatePermissions = async () => {
    const idsToUpdate = selectedRows.map((row) => row.id);
    const roleId = selectedRole ? parseInt(selectedRole, 10) : null;

    if (roleId) {
      try {
        await UserRoleService.bulkUpdateRoles(idsToUpdate, roleId);
        toast.success(t('settings.user_perms_updated'));
        setSelectedRows([]);
        setIsModalOpen(false);
        fetchOrganizationData();
      } catch {
        toast.error(t('settings.update_perms'));
      }
    }
  };

  const handleMenuItemClick = (roleId: string) => {
    setSelectedRole(roleId);
    setModalType('permissions');
    setIsModalOpen(true);
  };

  let modalContent = null;

  if (modalType === 'manage') {
    modalContent = (
      <Typography sx={{ minWidth: '400px' }}>
        {t('settings.bulk_user_remove_confirmation', {
          count: selectedRows.length,
          msg: selectedRows.length > 1 ? 'users' : 'user'
        })}
      </Typography>
    );
  } else if (modalType === 'permissions' && selectedRole) {
    modalContent = (
      <Typography sx={{ minWidth: '400px' }}>
        {t('settings.change_role_message', {
          role: (assignableRoles as Role[]).find((role) => role.id === parseInt(selectedRole, 10))?.name
        })}
      </Typography>
    );
  }

  let modalTitle = '';

  if (modalType === 'manage') {
    modalTitle = t('settings.manage_rows');
  } else if (modalType === 'permissions' && selectedRole) {
    if (selectedRows.length === 1) {
      modalTitle = t('settings.change_role_message', {
        role: (assignableRoles as Role[]).find((role) => role.id === parseInt(selectedRole, 10))?.name
      });
    } else {
      modalTitle = t('settings.bulk_role_change_confirmation', { count: selectedRows.length });
    }
  }

  let modalActions = null;

  if (modalType === 'manage') {
    modalActions = (
      <>
        <Button color='neutral' onClick={handleCloseModal} variant='plain'>
          {t('edit_profile.cancel')}
        </Button>
        <Button color='danger' onClick={handleRemoveUsers} variant='solid'>
          {t('settings.remove_users', {
            rows: selectedRows.length,
            msg: selectedRows.length > 1 ? 'users' : 'user'
          })}
        </Button>
      </>
    );
  } else if (modalType === 'permissions') {
    modalActions = (
      <>
        <Button color='neutral' onClick={handleCloseModal} variant='plain'>
          {t('edit_profile.cancel')}
        </Button>
        <ContrastButton onClick={handleUpdatePermissions}>{t('settings.confirm')}</ContrastButton>
      </>
    );
  }

  return (
    <>
      <TableHeader
        title={
          selectedRows.length > 0
            ? t('settings.manage_rows', {
                count: selectedRows.length,
                msg: selectedRows.length > 1 ? 'Users' : 'User'
              })
            : t('navigation.users_permissions')
        }
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1, ml: 2 }}>
          <Input
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('settings.search')}
            startDecorator={<SearchIcon />}
            sx={{
              '--Input-minHeight': '2.5rem',
              maxWidth: '25rem',
              width: '100%'
            }}
            value={searchQuery}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', marginLeft: 'auto', gap: 1 }}>
          <Button color='neutral' onClick={() => setIsInviteOpen(true)} size='md' variant='plain'>
            {t('settings.invite_users')}
          </Button>
        </Box>
      </TableHeader>

      {filteredData.length === 0 ? (
        <Box sx={{ py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
          <Typography level='title-md'>
            {debouncedQuery
              ? t('settings.no_results')
              : organizationData.users.length + organizationData.invitations.length === 0
              ? t('settings.no_users')
              : t('settings.no_results')}
          </Typography>
          {debouncedQuery ? (
            <Button size='sm' variant='outlined' color='neutral' onClick={() => setSearchQuery('')}>
              {t('settings.clear_search')}
            </Button>
          ) : (
            <Button size='sm' variant='plain' onClick={() => setIsInviteOpen(true)}>
              {t('settings.invite_users')}
            </Button>
          )}
        </Box>
      ) : (
        <DynamicTable
          columns={columns}
          currentUser={sessionStore.user as User}
          data={filteredData}
          checkbox={false}
          selectable={false}
          selectedRows={[] as any}
        />
      )}

      <GeneralModal
        actions={modalActions}
        content={modalContent}
        onClose={handleCloseModal}
        open={isModalOpen}
        title={modalTitle}
      />

      <Modal open={isInviteOpen} onClose={() => setIsInviteOpen(false)}>
        <ModalDialog sx={{ minWidth: 480 }}>
          <Typography level='title-lg' sx={{ mb: 1 }}>
            {t('settings.invite_users')}
          </Typography>
          <EmailInvitesModal onClose={() => setIsInviteOpen(false)} onInviteSuccess={fetchOrganizationData} />
        </ModalDialog>
      </Modal>

      <Modal open={isRoleDialogOpen} onClose={closeRoleDialog}>
        <ModalDialog sx={{ minWidth: 420 }}>
          <Typography level='title-lg' sx={{ mb: 1 }}>
            {t('settings.update_perms')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <Select
              placeholder={t('role_assignment.select_role')}
              value={roleDialogSelectedRole || ''}
              onChange={(_e, value) => setRoleDialogSelectedRole(value as string)}
              sx={{ minWidth: 220 }}
            >
              {(assignableRoles as Role[]).map((r) => (
                <Option key={r.id} value={String(r.id)}>
                  {r.name}
                </Option>
              ))}
            </Select>
            <Button onClick={handleSaveRoleDialog} disabled={!roleDialogSelectedRole} variant='solid'>
              {t('general_button_labels.save')}
            </Button>
            <Button color='neutral' variant='plain' onClick={closeRoleDialog}>
              {t('edit_profile.cancel')}
            </Button>
          </Box>
        </ModalDialog>
      </Modal>
    </>
  );
});

export default Settings;
